import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackClipSearchStarted, trackClipSearchCompleted, trackClipResultSelected } from '../services/analytics';
import { Toast, Field, PageHeader } from './v2-utils';
import '../styles/v2.css';

/* ── Image helpers ── */
function resolveImageSrc(src) {
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('data:image/')) return src;
  if (src.startsWith('/9j/'))   return `data:image/jpeg;base64,${src}`;
  if (src.startsWith('iVBOR')) return `data:image/png;base64,${src}`;
  if (src.startsWith('R0lGOD')) return `data:image/gif;base64,${src}`;
  if (src.startsWith('UklGR')) return `data:image/webp;base64,${src}`;
  return `data:image/jpeg;base64,${src}`;
}

async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 1024;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        let dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        if (dataUrl.length > 10 * 1024 * 1024) dataUrl = canvas.toDataURL('image/jpeg', 0.5);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AddProductV2() {
  const navigate = useNavigate();
  const fileInputRef    = useRef(null);
  const cameraInputRef  = useRef(null);
  const blurTimeoutRef  = useRef(null);
  const justSelectedRef = useRef(false);

  /* form */
  const [productName, setProductName]       = useState('');
  const [brand, setBrand]                   = useState('');
  const [variety, setVariety]               = useState('');
  const [size, setSize]                     = useState('');

  /* search */
  const [globalResults, setGlobalResults]   = useState([]);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [isOnline, setIsOnline]             = useState(navigator.onLine);

  /* image */
  const [capturedImage, setCapturedImage]   = useState(null);
  const [imagePreview, setImagePreview]     = useState(null);

  /* CLIP */
  const [clipResults, setClipResults]       = useState([]);
  const [clipLoading, setClipLoading]       = useState(false);

  /* ui */
  const [submitting, setSubmitting]         = useState(false);
  const [toast, setToast]                   = useState(null);

  /* online / offline */
  useEffect(() => {
    const up   = () => { setIsOnline(true);  setToast({ message: 'Back online.', severity: 'success' }); };
    const down = () => { setIsOnline(false); setToast({ message: 'You are offline. Some features are disabled.', severity: 'warning' }); };
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up); window.removeEventListener('offline', down);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  /* global product search */
  useEffect(() => {
    if (productName.length > 2 && isOnline && !justSelectedRef.current) {
      axiosInstance.get(`/products/search/global?q=${encodeURIComponent(productName)}`)
        .then(r => setGlobalResults(r.data || []))
        .catch(() => setGlobalResults([]));
    } else {
      setGlobalResults([]);
    }
    if (justSelectedRef.current) justSelectedRef.current = false;
  }, [productName, isOnline]);

  /* brand suggestions */
  useEffect(() => {
    if (productName.length > 1 && isOnline) {
      axiosInstance.get(`/products/brands/${encodeURIComponent(productName)}`)
        .then(r => setBrandSuggestions(r.data.brands || []))
        .catch(() => setBrandSuggestions([]));
    } else {
      setBrandSuggestions([]);
    }
  }, [productName, isOnline]);

  /* CLIP v2 search */
  const searchWithCLIP = async (imageData) => {
    if (!isOnline) return;
    const clipStart = Date.now();
    trackClipSearchStarted('image');
    setClipLoading(true);
    setClipResults([]);
    try {
      const res = await axiosInstance.post('/clipv2/search', { image: imageData, topK: 10 }, { timeout: 30000 });
      if (res.data.success && res.data.results?.length > 0) {
        setClipResults(res.data.results);
        trackClipSearchCompleted(res.data.results.length, Date.now() - clipStart, res.data.results[0]?.similarity ?? res.data.results[0]?.score);
        setToast({ message: `Found ${res.data.results.length} similar products via AI image search.`, severity: 'success' });
      } else {
        trackClipSearchCompleted(0, Date.now() - clipStart, null);
        setToast({ message: 'No similar products found. Fill in the form manually.', severity: 'info' });
      }
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED' || err.response?.status === 408;
      setToast({ message: isTimeout ? 'AI search timed out. Please try again.' : 'AI search failed. Fill in the form manually.', severity: 'warning' });
    } finally {
      setClipLoading(false);
    }
  };

  /* image handling */
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ message: 'Please select a valid image file.', severity: 'error' }); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: 'Image too large (max 5MB original). Please choose a smaller image.', severity: 'error' }); return;
    }
    try {
      const compressed = await compressImage(file);
      setCapturedImage(compressed);
      setImagePreview(compressed);
      if (isOnline) searchWithCLIP(compressed);
    } catch {
      setToast({ message: 'Failed to process image.', severity: 'error' });
    }
  };

  /* auto-fill from search or CLIP suggestion */
  const autoFill = (item, source = 'search') => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
    justSelectedRef.current = true;
    setProductName(item.product_name || '');
    setBrand(item.brand || '');
    setVariety(item.variety || '');
    setSize(item.size || '');
    setGlobalResults([]);
    setClipResults([]);
    setToast({ message: `Auto-filled from ${source === 'clip' ? 'AI suggestion' : 'existing product'}: ${item.product_name}`, severity: 'success' });
  };

  /* submit */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOnline) { setToast({ message: 'Cannot add product while offline.', severity: 'error' }); return; }
    if (capturedImage && capturedImage.length > 10 * 1024 * 1024) {
      setToast({ message: 'Image is too large. Please try a smaller image.', severity: 'error' }); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        product_name: productName.trim(),
        variety: variety.trim(),
        brand: brand.trim() || null,
        size: size.trim(),
        ...(capturedImage ? { image: capturedImage } : {}),
      };
      const res = await axiosInstance.post('/products', payload);
      const newProductId = res.data?.product_id;
      setToast({ message: 'Product added successfully!', severity: 'success' });
      setTimeout(() => {
        navigate(`/new/dashboard/units/add?product_id=${newProductId}&from_product=true`);
      }, 800);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message;
      if (!err.response) setToast({ message: 'Network error. Check your connection and try again.', severity: 'error' });
      else if (status === 413) setToast({ message: 'Image too large. Please try a smaller image.', severity: 'error' });
      else if (status === 408 || err.code === 'ECONNABORTED') setToast({ message: 'Upload timed out. Try a smaller image or check your connection.', severity: 'error' });
      else setToast({ message: msg || 'Failed to add product.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHeader title="Add Product" subtitle="Add a new product to your catalogue"
        onBack={() => navigate('/new/dashboard/products/view')} />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Image card (first — AI auto-fill before manual entry) ── */}
          <div className="v2-card" style={{ padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Product Image</p>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 }}>
              Take or upload a photo to automatically identify the product using AI. Image upload is optional.
            </p>

            {/* Image preview */}
            {imagePreview && (
              <div style={{ position: 'relative', marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
                <img src={imagePreview} alt="Preview" style={{ width: '100%', maxHeight: 240, objectFit: 'contain', background: '#f8fafc', display: 'block' }} />
                <button type="button" onClick={() => { setCapturedImage(null); setImagePreview(null); setClipResults([]); }}
                  style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            )}

            {/* Upload buttons */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />

              <button type="button" onClick={() => cameraInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" /></svg>
                Take Photo
              </button>

              <button type="button" onClick={() => fileInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
                Upload from Library
              </button>
            </div>

            {/* CLIP loading state */}
            {clipLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '12px 16px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #bfdbfe', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 13, color: '#1d4ed8', fontWeight: 500 }}>AI is searching for similar products…</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
          </div>

          {/* ── CLIP suggestions ── */}
          {clipResults.length > 0 && (
            <div className="v2-card" style={{ padding: 24, overflow: 'hidden' }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>AI Product Suggestions</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8' }}>Click a suggestion to auto-fill the form. Review before submitting.</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                {clipResults.map((result, i) => {
                  const imgSrc = resolveImageSrc(result.image_url);
                  return (
                    <button key={i} type="button" onClick={() => { trackClipResultSelected(i + 1); autoFill({ product_name: result.product_name, brand: result.brand, variety: result.variety, size: result.size }, 'clip'); }}
                      style={{ border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: 'white', cursor: 'pointer', textAlign: 'left', padding: 0, transition: 'border-color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}>
                      <div style={{ height: 90, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {imgSrc ? (
                          <img src={imgSrc} alt={result.product_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <svg style={{ width: 28, height: 28, color: '#cbd5e1' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909" /></svg>
                        )}
                      </div>
                      <div style={{ padding: '8px 10px' }}>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.product_name}</p>
                        {result.brand && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.brand}</p>}
                        {result.variety && <p style={{ margin: '1px 0 0', fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.variety}</p>}
                        {result.similarity != null && (
                          <p style={{ margin: '4px 0 0', fontSize: 10, fontWeight: 600, color: '#10b981' }}>{(result.similarity * 100).toFixed(0)}% match</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={() => setClipResults([])}
                style={{ marginTop: 12, fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Dismiss suggestions
              </button>
            </div>
          )}

          {/* ── Product details card (second — review/edit after AI auto-fill) ── */}
          <div className="v2-card" style={{ padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 20px' }}>Product Details</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {/* Product name with global search */}
              <Field label="Product Name *" hint="Type to search existing products and avoid duplicates">
                <div style={{ position: 'relative' }}>
                  <input className="v2-input" type="text" placeholder="e.g. Coca-Cola"
                    value={productName}
                    onChange={e => { setProductName(e.target.value); justSelectedRef.current = false; }}
                    onBlur={() => { blurTimeoutRef.current = setTimeout(() => setGlobalResults([]), 150); }}
                    onFocus={() => { if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; } }}
                    required />
                  {globalResults.length > 0 && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '8px 14px 4px' }}>Existing Products</p>
                      {globalResults.slice(0, 6).map((p, i) => (
                        <button key={i} type="button" onMouseDown={() => autoFill(p, 'search')}
                          style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '9px 14px', background: 'none', border: 'none', borderBottom: i < Math.min(globalResults.length, 6) - 1 ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', textAlign: 'left' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b' }}>{p.product_name}</span>
                          <span style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                            {[p.brand && `Brand: ${p.brand}`, p.variety && `Variety: ${p.variety}`, p.size && `Size: ${p.size}`].filter(Boolean).join(' · ') || 'No details'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>

              {/* Brand with chips */}
              <Field label="Brand" hint="Leave blank if the product has no brand">
                <input className="v2-input" type="text" placeholder="e.g. Coca-Cola, Unilever…"
                  value={brand} onChange={e => setBrand(e.target.value)} />
                {brandSuggestions.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 6px', fontWeight: 500 }}>Existing brands for "{productName}":</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {brandSuggestions.map(b => (
                        <button key={b} type="button" onClick={() => setBrand(b)}
                          style={{ padding: '4px 10px', borderRadius: 99, border: `1.5px solid ${brand === b ? '#10b981' : '#e2e8f0'}`, background: brand === b ? '#f0fdf4' : 'white', color: brand === b ? '#065f46' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Variety" hint="e.g. Gala, Granny Smith">
                  <input className="v2-input" type="text" placeholder="Optional" value={variety} onChange={e => setVariety(e.target.value)} />
                </Field>
                <Field label="Size / Description" hint="e.g. 300ml, 500g, Large">
                  <input className="v2-input" type="text" placeholder="Optional" value={size} onChange={e => setSize(e.target.value)} />
                </Field>
              </div>
            </div>
          </div>

          {/* ── Submit ── */}
          <button type="submit" className="v2-btn-submit" disabled={submitting || !productName.trim()} style={{ fontSize: 15 }}>
            {submitting ? 'Adding Product…' : 'Add Product'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', margin: 0 }}>
            After adding, you'll be taken to set up buying and selling units for this product.
          </p>
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
